import type {
  AgentDefInfo,
  AgentTemplate,
  AgentUsageSummary,
  AvailableProviderInfo,
  CapabilitiesResponse,
  CreateAgentDefBody,
  CreateAgentTemplateBody,
  CreateWorkspaceProviderBody,
  ModelProfileInfo,
  PromptPreview,
  PromptVersion,
  SystemProviderInfo,
  UpdateAgentDefBody,
} from '../types/api'
import { api } from './client'

export const capabilitiesApi = {
  list: () => api.get<CapabilitiesResponse>('/api/capabilities'),
}

export const providersApi = {
  list: () => api.get<SystemProviderInfo[]>('/api/admin/providers'),
  listAvailable: (workspaceId?: string) =>
    api.get<AvailableProviderInfo[]>(
      `/api/providers${workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
  listModels: (providerId: string, opts?: { workspaceId?: string }) =>
    api.get<string[]>(
      `/api/providers/${encodeURIComponent(providerId)}/models${opts?.workspaceId ? `?workspace_id=${encodeURIComponent(opts.workspaceId)}` : ''}`,
    ),
  /** What `model` takes on the provider. Hugging Face ids keep their `/`. */
  modelProfile: (
    providerId: string,
    model: string,
    opts?: { workspaceId?: string },
  ) =>
    api.get<ModelProfileInfo>(
      `/api/providers/${encodeURIComponent(providerId)}/models/${model.split('/').map(encodeURIComponent).join('/')}/profile${opts?.workspaceId ? `?workspace_id=${encodeURIComponent(opts.workspaceId)}` : ''}`,
    ),
  create: (body: {
    name: string
    provider_type?: string
    api_key?: string
    base_url?: string
  }) => api.post<SystemProviderInfo>('/api/admin/providers', body),
  update: (
    providerId: string,
    body: { api_key?: string | null; base_url?: string | null },
  ) => api.patch<void>(`/api/admin/providers/${providerId}`, body),
  delete: (providerId: string) =>
    api.delete<void>(`/api/admin/providers/${providerId}`),
}

export const agentDefsApi = {
  listForWorkspace: (
    workspaceId: string,
    opts?: { includeDeleted?: boolean },
  ) =>
    api.get<AgentDefInfo[]>(
      `/api/workspaces/${workspaceId}/agents${opts?.includeDeleted ? '?include_deleted=true' : ''}`,
    ),
  create: (workspaceId: string, body: CreateAgentDefBody) =>
    api.post<AgentDefInfo>(`/api/workspaces/${workspaceId}/agents`, body),
  createWithMeta: (workspaceId: string, body: CreateAgentDefBody) =>
    api.postWithMeta<AgentDefInfo | undefined>(
      `/api/workspaces/${workspaceId}/agents`,
      body,
    ),
  get: (workspaceId: string, agentId: string) =>
    api.get<AgentDefInfo>(`/api/workspaces/${workspaceId}/agents/${agentId}`),
  update: (workspaceId: string, agentId: string, body: UpdateAgentDefBody) =>
    api.patch<void>(`/api/workspaces/${workspaceId}/agents/${agentId}`, body),
  updateWithMeta: (
    workspaceId: string,
    agentId: string,
    body: UpdateAgentDefBody,
  ) =>
    api.patchWithMeta<AgentDefInfo>(
      `/api/workspaces/${workspaceId}/agents/${agentId}`,
      body,
    ),
  reconcileWithMeta: (workspaceId: string, agentId: string) =>
    api.patchWithMeta<AgentDefInfo>(
      `/api/workspaces/${workspaceId}/agents/${agentId}`,
      {},
    ),
  delete: (workspaceId: string, agentId: string) =>
    api.delete<void>(`/api/workspaces/${workspaceId}/agents/${agentId}`),
  deleteWithMeta: (workspaceId: string, agentId: string) =>
    api.deleteWithMeta<AgentDefInfo>(
      `/api/workspaces/${workspaceId}/agents/${agentId}`,
    ),
  listWorkspaceUsage: (workspaceId: string) =>
    api.get<AgentUsageSummary[]>(`/api/workspaces/${workspaceId}/usage`),
  listPromptVersions: (workspaceId: string, agentId: string) =>
    api.get<PromptVersion[]>(
      `/api/workspaces/${workspaceId}/agents/${agentId}/prompt-versions`,
    ),
  getPromptPreview: (workspaceId: string, agentId: string) =>
    api.get<PromptPreview>(
      `/api/workspaces/${workspaceId}/agents/${agentId}/prompt-preview`,
    ),
}

export const workspaceProvidersApi = {
  list: (wsId: string) =>
    api.get<SystemProviderInfo[]>(`/api/workspaces/${wsId}/providers`),
  create: (wsId: string, body: CreateWorkspaceProviderBody) =>
    api.post<SystemProviderInfo>(`/api/workspaces/${wsId}/providers`, body),
  update: (
    wsId: string,
    providerId: string,
    body: { api_key?: string | null; base_url?: string | null },
  ) => api.patch<void>(`/api/workspaces/${wsId}/providers/${providerId}`, body),
  delete: (wsId: string, providerId: string) =>
    api.delete<void>(`/api/workspaces/${wsId}/providers/${providerId}`),
}

export const agentTemplatesApi = {
  list: (wsId: string) =>
    api.get<AgentTemplate[]>(`/api/workspaces/${wsId}/agent-templates`),
  create: (wsId: string, body: CreateAgentTemplateBody) =>
    api.post<AgentTemplate>(`/api/workspaces/${wsId}/agent-templates`, body),
  delete: (wsId: string, id: string) =>
    api.delete<void>(`/api/workspaces/${wsId}/agent-templates/${id}`),
}
